import { apiFetch } from "../api/client";
import { registrationChannel } from "./registrationChannel";
import { formatFullName } from "./nomAffiche";

/**
 * LES COLLABORATEURS — les personnes des sociétés du porteur, qu'il peut inscrire comme
 * accompagnants sans ressaisir leur nom.
 *
 *   GET /domains/:d/events/:e/users/associates/me   apix
 *
 * ⚠️ C'EST LA ROUTE DE LA V1, TELLE QUELLE (`fo-meeting/src/api/v2/users.js`,
 * `getMyAssociates`, ouverte par `screens/steps/Confirmation/addAssociates.jsx` quand
 * `FO_MEETING_COMPANIONS_ASSOCIATES_IS_ACTIVE` est vraie). Ce qu'elle rend, lu dans
 * `api/src/routes/users/getMyAssociates` :
 *
 * - les employés de TOUTES les sociétés où le porteur est lui-même employé, et pas
 *   seulement celle retenue pour l'événement ;
 * - sans les archivés (compte, rattachement, société) ;
 * - sans le porteur lui-même, reconnu à son `authenticationId` ;
 * - avec `isRegistered`, lu sur SA participation à CET événement.
 *
 * ⚠️ LA PERSONNE N'APPARAÎT QU'UNE FOIS (`groupBy U2.id`), même employée de deux de ses
 * sociétés : la société affichée est alors l'une des deux, celle que MySQL retient.
 */

export type Coworker = {
  id: number;
  /** « Prénom Nom », jamais vide : voir `readCompanions`, même raison. */
  name: string;
  /** La société par laquelle on la connaît, pour distinguer deux homonymes. */
  company: string;
  /**
   * Déjà inscrite à l'événement, par elle-même ou par quelqu'un d'autre. La V1 grise
   * alors son bouton « Choisir » : la rattacher ici la retirerait à celui qui l'avait.
   */
  registered: boolean;
};

type Raw = {
  id?: unknown;
  firstName?: unknown;
  lastName?: unknown;
  name?: unknown;
  isRegistered?: unknown;
};

const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");

/**
 * CE QUE LA ROUTE REND, RAMENÉ À CE QUE LA LISTE AFFICHE.
 *
 * ⚠️ LE PORTEUR EST RETIRÉ UNE SECONDE FOIS, par son identifiant. La route l'exclut par
 * son compte de connexion : c'est un filet, pour qu'il ne se propose jamais à lui-même
 * si cette exclusion venait à manquer.
 *
 * ⚠️ `isRegistered` VIENT DE MYSQL : 1, 0 ou `null` (aucune participation). Seul un vrai
 * oui grise la ligne.
 */
export function readCoworkers(
  raw: unknown,
  bearer: number | undefined,
): Coworker[] {
  const list = Array.isArray(raw) ? (raw as Raw[]) : [];
  const seen = new Set<number>();
  return list.flatMap((u) => {
    if (typeof u?.id !== "number" || u.id === bearer || seen.has(u.id)) return [];
    seen.add(u.id);
    // Format d'affichage seulement : « Prénom NOM ». Rien n'est renvoyé à l'API sous cette forme.
    const name = formatFullName(text(u.firstName), text(u.lastName));
    return [
      {
        id: u.id,
        name: name || `Accompagnant ${u.id}`,
        company: text(u.name),
        registered: u.isRegistered === true || u.isRegistered === 1,
      },
    ];
  });
}

export async function fetchCoworkers(
  domainId: number,
  eventId: number,
  bearer: number,
  signal?: AbortSignal,
): Promise<Coworker[]> {
  const answer = await apiFetch<{ users?: unknown } | null>(
    `/domains/${domainId}/events/${eventId}/users/associates/me`,
    { api: "apix", signal },
  );
  return readCoworkers(answer?.users, bearer);
}

/**
 * LE RATTACHE AU PORTEUR — ce qui fait de lui un accompagnant.
 *
 *   PUT /domains/:d/events/:e/users/:collaborateur   api-v1
 *
 * ⚠️ LES DEUX ÉCRITURES DE LA V1 EN UNE. Elle posait `registeredBy2` au choix
 * (`addAssociates.jsx`), puis, à la fin de son formulaire, « inscrit par »,
 * `registeredFrom`, la présence sur place et la typologie du porteur
 * (`screens/steps/Informations.jsx`). La V2 inscrit un accompagnant au moment où on
 * l'ajoute : tout part donc ici, au choix.
 *
 * ⚠️ CETTE ROUTE CRÉE LA PARTICIPATION SI ELLE MANQUE, avec son `code` et son
 * `voucherCode` (`UserEventService.createOrUpdateOne`) : le collaborateur a son badge,
 * comme un accompagnant créé de toutes pièces. Elle refuse (400) quand la jauge est
 * pleine.
 */
export async function attachCoworker(
  domainId: number,
  eventId: number,
  bearer: { id: number; firstName?: string | null; lastName?: string | null },
  coworkerId: number,
  heritage: { typoId?: number },
): Promise<void> {
  const signature = `${bearer.firstName || ""} ${bearer.lastName || ""} (${bearer.id})`;
  await apiFetch(
    `/domains/${domainId}/events/${eventId}/users/${coworkerId}`,
    {
      method: "PUT",
      body: {
        registeredBy: signature,
        registeredBy2: bearer.id,
        registeredFrom: registrationChannel(),
        isRemote: false,
        ...(heritage.typoId !== undefined ? { typoId: heritage.typoId } : {}),
      },
    },
  );
}
